import { memo, useCallback, useContext, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import {
  BiChatContextType,
  BrandInsightChatContexts,
  ChatContextType,
  ChatCustomFilter,
  ChatFilterOperator,
  ChatSource,
  ChatSourcesMap,
  type BrandInsightsInstanceContext,
  type AgentDataSource,
  type AgentFilter,
  type ContextFilter,
  type CustomFilter,
  type ProjectFilter,
  type RecordFilter,
  type SourceFilter,
} from '@trinity/chat';
import {
  ActiveChatSessionContext,
  ChatContextItemsContext,
  ChatSessionFiltersContext,
} from '@trinity/chat-components/components/Chat/context';
import { useAgentResources } from '@trinity/chat-components/components/Chat.Header/AgentResources.Container.js';
import { HeaderContextContext } from '@trinity/chat-components/components/Chat.Header/context';
import { ContextSearch } from '@trinity/chat-components/components/Chat.Header/ContextSearch';
import { ChatContextSearchContainer } from '@trinity/chat-components/components/Chat.Header/ContextSearch.Container';
import { SimpleContextSelector } from '@trinity/chat-components/components/Chat.Header/SimpleContextSelector';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import {
  generateEmptyChatSessionFiltersState,
  getChatSourceFilterOptions,
} from '@trinity/chat-components/components/Chat.State/state.initial-state';
import { useAvatarPanel } from '@trinity/chat-components/components/DigitalTwin/AvatarPanel.context';
import { GroupFeaturesContext, useGroupFeatures } from '@trinity/chat-components/containers/Chat';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { AgentCustomFilterValueSelector } from './AgentCustomFilter.Value';
import { AgentCustomFilters } from './AgentCustomFilters';
import { isSubjectScopedContext } from './common';
import { Context } from './Context';
import { DataSource } from './DataSource';
import { isFilterEnabledForContext } from './filterLabelMapping';
import type { AgentFilterItem } from './interfaces';
import { FilterTypeEnum } from './interfaces';
import { PendingContextContext } from './PendingContext.context';
import * as Presentation from './Presentation';
import { Project } from './Project';
import { PublishedDate } from './PublishedDate';
import { Record } from './Record';
import { Source } from './Source';
import { Tag } from './Tag';

// Maps a normalised subject.kind value (lowercase) to the corresponding ChatSource enum.
// Used by SidePanelSourceController to lock the Record Type filter when viewing a specific record.
const KIND_TO_CHAT_SOURCE: Partial<Record<string, ChatSource>> = {
  transcript: ChatSource.Transcripts,
  transcripts: ChatSource.Transcripts,
  presentation: ChatSource.Presentations,
  presentations: ChatSource.Presentations,
  survey: ChatSource.Surveys,
  surveys: ChatSource.Surveys,
  survey_analysis: ChatSource.SurveyAnalyses,
  survey_analyses: ChatSource.SurveyAnalyses,
  dashboard: ChatSource.Dashboards,
  dashboards: ChatSource.Dashboards,
  text: ChatSource.Text,
  profile: ChatSource.Profiles,
  profiles: ChatSource.Profiles,
};

type Props = {
  active: boolean;
  isSidePanel?: boolean;
};

export function FilterPane(props: Props) {
  const agentResources = useAgentResources();
  const { isDigitalTwin, interactionMode } = useAvatarPanel();
  const { context } = useContext(ActiveChatSessionContext);
  const isDTVideo = isDigitalTwin && interactionMode === 'video';
  const { isSidePanel } = props;

  // A Context pick awaiting a subject before it commits (see ContextFilterController/
  // ProjectController). Not dispatched, so an abandoned pick just evaporates on unmount.
  const [pendingContext, setPendingContext] = useState<BrandInsightsInstanceContext.ItemWithMetadata | null>(
    null
  );

  const shouldShowStandardFilters = useMemo(() => {
    if (isSidePanel) return true;
    return (
      context.type === ChatContextType.Agent && BrandInsightChatContexts.includes(context.data.identifier)
    );
  }, [context, isSidePanel]);

  if (agentResources.isLoading) {
    return null;
  }

  return (
    <PendingContextContext.Provider value={{ pendingContext, setPendingContext }}>
      <Root isSidePanel={isSidePanel}>
        {!isSidePanel && (
          <Header>
            <HeaderLabel>Source Filters</HeaderLabel>
            <ResetController {...props} />
          </Header>
        )}
        {isDTVideo && <DTContextSection />}
        <Filters>
          {shouldShowStandardFilters && (
            <>
              {!isSidePanel && <DataSourceController dataSources={agentResources.data.dataSources} />}
              <PublishedDateController />
              <ContextFilterController pendingContext={pendingContext} setPendingContext={setPendingContext} />
              <ProjectController pendingContext={pendingContext} setPendingContext={setPendingContext} />
              {!isSidePanel && <RecordController />}
              {isSidePanel ? <SidePanelSourceController /> : <SourceController />}
              <TagController isSidePanel={isSidePanel} />
            </>
          )}
          {!isSidePanel && (
            <>
              <UserTerritoriesController filters={agentResources.data.filters} />
              <AgentCustomFiltersController filters={agentResources.data.filters} />
            </>
          )}
        </Filters>
      </Root>
    </PendingContextContext.Provider>
  );
}

function DTContextSection() {
  const headerCtx = useContext(HeaderContextContext);
  const hasActiveSearch = !!headerCtx?.pendingContextType || !!headerCtx?.pendingEverythingSearch;

  return (
    <DTSection>
      <DTSectionLabel>Context</DTSectionLabel>
      <DTSectionContent>
        <SimpleContextSelector />
        <ChatContextSearchContainer>
          {hasActiveSearch ? <StyledContextSearch /> : null}
        </ChatContextSearchContainer>
      </DTSectionContent>
    </DTSection>
  );
}

type PendingContextProps = {
  pendingContext: BrandInsightsInstanceContext.ItemWithMetadata | null;
  setPendingContext: (context: BrandInsightsInstanceContext.ItemWithMetadata | null) => void;
};

function ProjectController({ pendingContext, setPendingContext }: PendingContextProps) {
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const { context: activeContext } = useContext(ActiveChatSessionContext);
  const brandIdentifier = useActiveBrandIdentifier();

  // A pending pick takes precedence so this field's mode (see Project.tsx's resolveMode)
  // matches what the user just selected rather than the still-active context.
  const context = pendingContext ?? activeContext;

  const enabled = useMemo(() => isFilterEnabledForContext(context), [context]);

  const value = useMemo(
    () => resolveSelectedProjects(context, filters.projects),
    [context, filters.projects]
  );

  // Document/Folder/Brand/Transcript contexts repurpose this field into a record search
  // (see Project.tsx's resolveMode); `filters.records` is only populated by RecordController
  // historically, so it can be undefined here.
  const records = filters.records ?? [];

  const handleRecordsChange = useCallback(
    (records: RecordFilter.Item[]) => {
      dispatch({
        type: 'filters/updated',
        payload: {
          filters: {
            records,
          },
        },
      });
    },
    [dispatch]
  );

  // Agent-context chats are scoped by `subject`, not `filters.records` — the query pipeline
  // only reads `context.data.subject`, so a selection here must update the active context.
  // This also commits a pending Context pick; clearing it just abandons it (no dispatch).
  const handleSubjectSelected = useCallback(
    (subject: { id: string; name: string } | null) => {
      if (context.type !== ChatContextType.Agent) {
        return;
      }

      if (subject === null && pendingContext) {
        setPendingContext(null);
        return;
      }

      dispatch({
        type: 'instance-context-changed',
        payload: {
          context: {
            type: ChatContextType.Agent,
            data: {
              ...context.data,
              subject: subject ?? undefined,
            },
            metadata: context.metadata ?? {},
          },
          brandIdentifier,
        },
      });
      setPendingContext(null);
    },
    [context, pendingContext, setPendingContext, dispatch, brandIdentifier]
  );

  return (
    <Project
      disabled={!enabled}
      context={context}
      value={value}
      onChange={(projects) => {
        dispatch({
          type: 'filters/updated',
          payload: {
            filters: {
              projects,
            },
          },
        });
      }}
      records={records}
      onRecordsChange={handleRecordsChange}
      onSubjectSelected={handleSubjectSelected}
    />
  );
}

function RecordController() {
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const session = useContext(ActiveChatSessionContext);
  const features = useContext(GroupFeaturesContext);
  const hasProfilesEnabled = features.hcpProfiles;

  const handleChange = useCallback(
    (records: RecordFilter.Item[]) => {
      dispatch({
        type: 'filters/updated',
        payload: {
          filters: {
            records,
          },
        },
      });
    },
    [dispatch]
  );

  if (!hasProfilesEnabled) {
    return null;
  }

  if (session.context.type !== ChatContextType.Global) {
    return null;
  }

  return (
    <Record
      context={session.context}
      projectIds={filters.projects.map((project) => project.id)}
      sourceTypes={filters.sources.map((source) => source.id)}
      onChange={handleChange}
      value={filters.records}
    />
  );
}

function AgentCustomFiltersController({ filters }: { filters?: AgentFilter[] }) {
  const session = useContext(ActiveChatSessionContext);

  if (session.context.type !== ChatContextType.Agent) {
    return null;
  }

  return <AgentCustomFilters context={session.context} filters={filters} />;
}

type AgentFilterValueWithMetadata = CustomFilter.ItemValue & {
  filterId?: number | string;
};

function UserTerritoriesController({ filters }: { filters?: AgentFilter[] }) {
  const session = useContext(ActiveChatSessionContext);
  const sessionFilters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const { agentIdentifier } = useAgentResources();
  const customFilters = useMemo(() => {
    return sessionFilters.custom || [];
  }, [sessionFilters.custom]);

  const territoryFilter = useMemo<AgentFilterItem | null>(() => {
    const foundFilter = filters?.find((filter) => filter.type === FilterTypeEnum.UserTerritory);
    if (!foundFilter) {
      return null;
    }

    return {
      id: foundFilter.id,
      identifier: foundFilter.identifier,
      label: foundFilter.label,
      type: FilterTypeEnum.UserTerritory,
      operators: foundFilter.operators,
      data: foundFilter.data,
    };
  }, [filters]);

  const territoryFilterIndex = useMemo(() => {
    if (!territoryFilter) {
      return -1;
    }

    return customFilters.findIndex((filter) => {
      if (filter.type !== ChatCustomFilter.AgentFilter) {
        return false;
      }

      const firstValue = filter.value[0] as AgentFilterValueWithMetadata | undefined;
      if (!firstValue?.filterId) {
        return false;
      }

      return Number(firstValue.filterId) === territoryFilter.id;
    });
  }, [customFilters, territoryFilter]);

  const value = useMemo(() => {
    if (territoryFilterIndex === -1) {
      return [];
    }

    const existingFilter = customFilters[territoryFilterIndex];
    if (!existingFilter || existingFilter.type !== ChatCustomFilter.AgentFilter) {
      return [];
    }

    return existingFilter.value;
  }, [customFilters, territoryFilterIndex]);

  const handleValuesChange = useCallback(
    (values: CustomFilter.ItemValue[]) => {
      if (!territoryFilter) {
        return;
      }

      const singleValue = values[0] ? [values[0]] : [];

      if (singleValue.length === 0) {
        if (territoryFilterIndex !== -1) {
          dispatch({
            type: 'filters/remove-custom-filter',
            payload: {
              id: territoryFilterIndex,
            },
          });
        }
        return;
      }

      const territoryCustomFilter: CustomFilter.AgentCustomFilter = {
        type: ChatCustomFilter.AgentFilter,
        operator: [ChatFilterOperator.Eq],
        value: singleValue.map((item) => ({
          ...item,
          filterId: territoryFilter.id,
          filterLabel: territoryFilter.label,
        })),
      };

      if (territoryFilterIndex === -1) {
        dispatch({
          type: 'filters/add-custom-filter',
          payload: {
            value: territoryCustomFilter,
          },
        });
        return;
      }

      dispatch({
        type: 'filters/update-custom-filter',
        payload: {
          id: territoryFilterIndex,
          value: territoryCustomFilter,
        },
      });
    },
    [dispatch, territoryFilter, territoryFilterIndex]
  );

  if (session.context.type !== ChatContextType.Agent) {
    return null;
  }

  if (!territoryFilter || !agentIdentifier) {
    return null;
  }

  return (
    <Presentation.Row>
      <Presentation.Label>{territoryFilter.label}</Presentation.Label>
      <Presentation.Value>
        <AgentCustomFilterValueSelector
          agentIdentifier={agentIdentifier}
          filter={territoryFilter}
          values={value}
          onValuesChange={handleValuesChange}
          readOnly={false}
        />
      </Presentation.Value>
    </Presentation.Row>
  );
}

function SourceController() {
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const { context } = useContext(ActiveChatSessionContext);
  const features = useGroupFeatures();

  const options = useMemo(() => getChatSourceFilterOptions({ context, features }), [context, features]);

  if (!options.length) return null;

  return (
    <Source
      options={options}
      value={filters.sources}
      onChange={(sources) => {
        dispatch({
          type: 'filters/updated',
          payload: {
            filters: {
              sources: sources.length ? sources : options,
            },
          },
        });
      }}
    />
  );
}

// Side-panel variant of SourceController.
// When the chat is scoped to a specific record (Transcript, or an Agent+Document subject),
// the Record Type filter is locked to the matching source type and grayed out.
function SidePanelSourceController() {
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const { context } = useContext(ActiveChatSessionContext);
  const features = useGroupFeatures();

  const lockedSource = useMemo((): SourceFilter.Item | null => {
    if (context.type === ChatContextType.Transcript) {
      return { id: ChatSource.Transcripts, name: ChatSourcesMap.get(ChatSource.Transcripts) ?? '' };
    }

    if (context.type === ChatContextType.Agent) {
      if (context.data.identifier === BiChatContextType.Transcript) {
        return { id: ChatSource.Transcripts, name: ChatSourcesMap.get(ChatSource.Transcripts) ?? '' };
      }
      if (context.data.identifier === BiChatContextType.Document && context.data.subject?.kind) {
        const chatSource = KIND_TO_CHAT_SOURCE[context.data.subject.kind.trim().toLowerCase()];
        if (chatSource) {
          return { id: chatSource, name: ChatSourcesMap.get(chatSource) ?? '' };
        }
      }
    }

    return null;
  }, [context]);

  const options = useMemo(() => getChatSourceFilterOptions({ context, features }), [context, features]);

  if (lockedSource) {
    return <Source options={[lockedSource]} value={[lockedSource]} readOnly onChange={() => undefined} />;
  }

  if (!options.length) return null;

  return (
    <Source
      options={options}
      value={filters.sources}
      onChange={(sources) => {
        dispatch({
          type: 'filters/updated',
          payload: {
            filters: {
              sources: sources.length ? sources : options,
            },
          },
        });
      }}
    />
  );
}

function PublishedDateController() {
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const { context } = useContext(ActiveChatSessionContext);
  const groupFeatures = useContext(GroupFeaturesContext);

  const defaultValue = useMemo(
    () =>
      generateEmptyChatSessionFiltersState({
        context,
        features: groupFeatures,
      }).minDocumentDate,
    [context, groupFeatures]
  );

  return (
    <PublishedDate
      defaultValue={defaultValue}
      value={filters.minDocumentDate}
      onChange={(minDocumentDate) => {
        dispatch({
          type: 'filters/updated',
          payload: {
            filters: {
              minDocumentDate,
            },
          },
        });
      }}
    />
  );
}

function ContextFilterController({ pendingContext, setPendingContext }: PendingContextProps) {
  const dispatch = useContext(ChatStateDispatchContext);
  const contextItems = useContext(ChatContextItemsContext);
  const { context: activeContext } = useContext(ActiveChatSessionContext);
  const brandIdentifier = useActiveBrandIdentifier();

  const handleChange = useCallback(
    (selectedValue: ContextFilter.Value) => {
      // Clearing (the select's "x" button) only ever needs to abandon an in-progress,
      // not-yet-committed pick — there's no "no context" state for an active chat to fall
      // back to, so this just reverts the display back to the real active context.
      if (!selectedValue) {
        setPendingContext(null);
        return;
      }

      if (contextItems && brandIdentifier) {
        const fullContextItem = contextItems.find((item) => item.identifier === selectedValue.identifier);
        if (fullContextItem) {
          const contextWithMetadata: BrandInsightsInstanceContext.ItemWithMetadata = {
            type: ChatContextType.Agent,
            data: fullContextItem,
            metadata: {},
          };

          // Subject-scoped kinds need a subject before this selection means anything —
          // hold it as pending until ProjectController picks one. Everything else commits now.
          if (isSubjectScopedContext(contextWithMetadata)) {
            setPendingContext(contextWithMetadata);
            return;
          }

          // Updates the active context and resets all filters in one state update.
          setPendingContext(null);
          dispatch({
            type: 'instance-context-changed',
            payload: {
              context: contextWithMetadata,
              brandIdentifier,
            },
          });
        }
      }
    },
    [dispatch, contextItems, brandIdentifier, setPendingContext]
  );

  if (!contextItems || contextItems.length === 0) {
    return null;
  }

  // Pending pick (not yet committed) takes precedence, then the active session context.
  const displayValue =
    (pendingContext?.type === ChatContextType.Agent
      ? { identifier: pendingContext.data.identifier, name: pendingContext.data.name }
      : null) ??
    (activeContext?.type === ChatContextType.Agent && activeContext?.data
      ? {
          identifier: activeContext.data.identifier,
          name: activeContext.data.name,
        }
      : null);

  return <Context value={displayValue} onChange={(context) => handleChange(context)} />;
}

function TagController({ isSidePanel }: { isSidePanel?: boolean }) {
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const { context } = useContext(ActiveChatSessionContext);

  return (
    <Tag
      context={context}
      value={filters.tags}
      isSidePanel={isSidePanel}
      onChange={(tags) => {
        dispatch({
          type: 'filters/updated',
          payload: {
            filters: {
              tags,
            },
          },
        });
      }}
    />
  );
}

function DataSourceController({ dataSources }: { dataSources?: AgentDataSource[] }) {
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const { context } = useContext(ActiveChatSessionContext);

  const shouldRenderFilter = useMemo(() => {
    if (context.type === ChatContextType.Agent) {
      return !BrandInsightChatContexts.includes(context.data.identifier);
    }
    return true;
  }, [context]);

  const transformedDataSources = useMemo(() => {
    if (!dataSources) return undefined;
    return dataSources.map((ds) => ({
      id: String(ds.id),
      name: ds.name,
    }));
  }, [dataSources]);

  const options = transformedDataSources || filters.dataSources;

  if (!shouldRenderFilter) {
    return null;
  }

  return (
    <DataSource
      context={context}
      options={options}
      value={filters.dataSources}
      dataSources={dataSources}
      onChange={(values) => {
        dispatch({
          type: 'filters/updated',
          payload: {
            filters: {
              dataSources: values.length ? values : options,
            },
          },
        });
      }}
    />
  );
}

const ResetController = memo(({ active }: Props) => {
  const dispatch = useContext(ChatStateDispatchContext);

  return (
    <HeaderReset disabled={!active} onClick={() => dispatch({ type: 'filters/reset' })}>
      Reset
    </HeaderReset>
  );
});

const Root = styled('div')<{ isSidePanel?: boolean }>(({ theme, isSidePanel }) =>
  isSidePanel
    ? {
        backgroundColor: theme.palette.white.main,
        boxSizing: 'border-box',
        padding: '12px 0',
        width: '100%',
      }
    : {
        backgroundColor: theme.palette.white.main,
        borderRadius: 5,
        border: `1px solid ${theme.palette.gray.light1}`,
        boxShadow: '0 2px 6px 2px rgba(60, 64, 67, .15)',
        boxSizing: 'border-box',
        padding: 12,
        minWidth: 450,
        maxWidth: 600,
      }
);

const Header = styled.div({
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: '10px',
});

const HeaderLabel = styled.h1(({ theme }) => ({
  fontSize: '18px',
  fontFamily: theme.fonts.semiBold,
}));

const HeaderReset = styled.button(({ disabled, theme }) => ({
  color: !disabled ? theme.palette.text.primary : theme.palette.gray.light1,
  fontSize: '14px',
  transition: 'text-decoration 150ms, color 150ms',

  ':hover': {
    textDecoration: disabled ? 'underline' : 'none',
  },
}));

const Filters = styled.div({
  boxSizing: 'border-box',
  padding: '0 2px',
});

const DTSection = styled.div(({ theme }) => ({
  borderTop: `1px solid ${theme.palette.gray.light2}`,
  borderBottom: `1px solid ${theme.palette.gray.light2}`,
  padding: '10px 2px',
  marginBottom: '10px',
}));

const DTSectionLabel = styled.div(({ theme }) => ({
  fontSize: '12px',
  fontFamily: theme.fonts.semiBold,
  color: theme.palette.gray.main,
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '8px',
}));

const DTSectionContent = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flexWrap: 'wrap',
});

const StyledContextSearch = styled(ContextSearch)({});

function resolveSelectedProjects(
  context: BrandInsightsInstanceContext.ItemWithMetadata,
  filtersProjects: ProjectFilter.Item[]
): ProjectFilter.Item[] {
  if (context.type === ChatContextType.Project) {
    return [{ id: context.data.projectId, name: context.metadata.name }];
  }

  if (context.type === ChatContextType.Agent && context.data.identifier === BiChatContextType.Project) {
    // Defensive guard: context.data.subject may be undefined in certain staging data states
    if (context.data.subject?.id && context.data.subject?.name) {
      return [{ id: context.data.subject.id as number, name: context.data.subject.name }];
    }
  }

  return filtersProjects;
}
